// Slice 2L · Company Job Calendar (owner-scoped, read-only).
//
// Pulls every scheduled event across the contractor's projects via
// GET /api/schedule-events and renders a month grid on desktop (>=768px)
// with an automatic switch to an agenda / list view on mobile (<768px,
// tested at 390px). Selecting an event navigates to that project's
// Project Detail page where the existing per-project Schedule card
// handles all mutations. This page never mutates schedule state.
//
// Product principle: "Levi recommends. The contractor decides." The
// company calendar is a read-only awareness surface — no drag-and-drop,
// no dispatch, no time clock, no inline delete/status changes.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import LeviScheduleAwareness from "@/components/schedule/LeviScheduleAwareness";
// PO 2026-02 · Job Calendar app-shell fix — the /app/schedule route
// must render inside the same authenticated shell (sidebar + topbar)
// used by Dashboard / Customers / Projects / Invoices. Wrapping the
// return in DashboardLayout was previously missing from this page.
import DashboardLayout from "@/components/layout/DashboardLayout";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  List as ListIcon,
  LayoutGrid,
  Loader2,
  RefreshCw,
  Filter as FilterIcon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Small time helpers — deliberately plain JS, no external date library.
// ---------------------------------------------------------------------------
const startOfDay = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};
const endOfDay = (d) => {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
};
const addDays = (d, n) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const sameYMD = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const isBefore = (a, b) => a.getTime() < b.getTime();
const isAfter = (a, b) => a.getTime() > b.getTime();

const monthLabel = (d, lang) => {
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-US", {
      month: "long",
      year: "numeric",
    }).format(d);
  } catch (_) {
    return `${d.getMonth() + 1}/${d.getFullYear()}`;
  }
};

const timeLabel = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-US", {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch (_) {
    return "";
  }
};

const dateHeader = (d, lang) => {
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    }).format(d);
  } catch (_) {
    return d.toDateString();
  }
};

// ---------------------------------------------------------------------------
// useIsMobile — media query hook. Default landing view flips automatically
// so a 390px viewport lands on Agenda, a 1440px viewport lands on Month.
// ---------------------------------------------------------------------------
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
};

// ---------------------------------------------------------------------------
// expandOccurrences — pure. Turn one seed event into its concrete
// occurrences within [rangeStart, rangeEnd]. Mirrors the backend's
// expand_recurrence semantics with the same 60-occurrence hard cap.
// ---------------------------------------------------------------------------
const RECURRENCE_MAX_OCCURRENCES = 60;
const RECURRENCE_MAX_DAYS = 365;

const addMonths = (d, n) => {
  const c = new Date(d);
  const targetMonth = c.getMonth() + n;
  c.setMonth(targetMonth);
  // If the target month has fewer days, JS auto-rolls to next month —
  // clamp back to the last day of the intended month (matches backend).
  if (c.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    c.setDate(0);
  }
  return c;
};

export const expandOccurrences = (event, rangeStart, rangeEnd) => {
  if (!event) return [];
  const s = event.start_at ? new Date(event.start_at) : null;
  const e = event.end_at ? new Date(event.end_at) : null;
  if (!s || !e || Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return [];
  const rule = (event.recurrence && event.recurrence.rule) || "none";
  const interval = Math.max(1, Number(event.recurrence?.interval) || 1);
  const count = event.recurrence?.count;
  const untilRaw = event.recurrence?.until;
  const until = untilRaw ? new Date(untilRaw) : null;
  const horizon = new Date(s.getTime() + RECURRENCE_MAX_DAYS * 24 * 3600 * 1000);
  const cap =
    typeof count === "number" && count > 0
      ? Math.min(RECURRENCE_MAX_OCCURRENCES, count)
      : RECURRENCE_MAX_OCCURRENCES;

  const out = [];
  const push = (os, oe) => {
    if (rangeEnd && os > rangeEnd) return "past";
    if (rangeStart && oe < rangeStart) return "before";
    out.push({ ...event, occ_start: os, occ_end: oe });
    return "in";
  };

  push(s, e);
  if (rule === "none" || !["daily", "weekly", "monthly"].includes(rule)) {
    return out;
  }
  let cs = new Date(s);
  let ce = new Date(e);
  while (out.length < cap) {
    if (rule === "daily") {
      cs = addDays(cs, interval);
      ce = addDays(ce, interval);
    } else if (rule === "weekly") {
      cs = addDays(cs, interval * 7);
      ce = addDays(ce, interval * 7);
    } else if (rule === "monthly") {
      cs = addMonths(cs, interval);
      ce = addMonths(ce, interval);
    } else break;
    if (until && cs > until) break;
    if (cs > horizon) break;
    const res = push(cs, ce);
    if (res === "past") break;
  }
  return out;
};

// ---------------------------------------------------------------------------
// buildMonthGrid — always returns a 6-week grid (42 days) starting on
// Sunday so the calendar height doesn't jump between months.
// ---------------------------------------------------------------------------
const buildMonthGrid = (cursor) => {
  const first = startOfMonth(cursor);
  const firstWeekday = first.getDay(); // 0 = Sun
  const gridStart = addDays(first, -firstWeekday);
  const days = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  return days;
};

// ---------------------------------------------------------------------------
// eventsOnDay — pure. Returns the occurrences of `events` that overlap
// `day` (any part of the day). All-day events count as spanning the day.
// ---------------------------------------------------------------------------
const eventsOnDay = (occurrences, day) => {
  const ds = startOfDay(day);
  const de = endOfDay(day);
  return occurrences.filter((o) => {
    const s = o.occ_start;
    const e = o.occ_end;
    if (!s || !e) return false;
    return s <= de && e >= ds;
  });
};

// ---------------------------------------------------------------------------
// Status → badge tone. Canceled uses muted styling; in_progress a warm
// accent; completed a de-emphasized tone; scheduled the default brand.
// ---------------------------------------------------------------------------
const statusTone = (status) => {
  switch (status) {
    case "in_progress":
      return "bg-amber-500/15 text-amber-800 border-amber-500/30";
    case "completed":
      return "bg-emerald-500/10 text-emerald-800 border-emerald-500/30";
    case "canceled":
      return "bg-neutral-200 text-neutral-500 border-neutral-300 line-through";
    default:
      return "bg-[var(--jc-brand-tint,#eef2ff)] text-[var(--jc-text,#0f172a)] border-[var(--jc-brand,#4f46e5)]/30";
  }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const EventPill = ({ occ, t, lang, dense = false }) => {
  const projectId = occ.project_id || occ?.project?.id || "";
  const label =
    (occ.title && occ.title.trim()) ||
    (occ.project?.customer_name && occ.project.customer_name.trim()) ||
    t("schedule.company.customer_untitled");
  const time = occ.all_day ? "" : timeLabel(occ.occ_start?.toISOString?.() || occ.start_at, lang);
  const customer = (occ.project?.customer_name || "").trim();
  const tone = statusTone(occ.status);
  if (!projectId) {
    return (
      <div
        data-testid="calendar-event-pill"
        className={`text-[11px] leading-tight rounded border px-1.5 py-0.5 ${tone} ${dense ? "truncate" : ""}`}
      >
        {time ? <span className="font-medium mr-1">{time}</span> : null}
        <span>{label}</span>
      </div>
    );
  }
  return (
    <Link
      to={`/app/projects/${projectId}`}
      data-testid="calendar-event-pill"
      data-event-id={occ.id}
      data-project-id={projectId}
      className={`block text-[11px] leading-tight rounded border px-1.5 py-0.5 hover:brightness-95 transition ${tone} ${dense ? "truncate" : ""}`}
      title={customer ? `${label} · ${customer}` : label}
    >
      {time ? <span className="font-medium mr-1">{time}</span> : null}
      <span>{label}</span>
    </Link>
  );
};

const MonthView = ({ cursor, occurrences, t, lang }) => {
  const days = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const monthNum = cursor.getMonth();
  const today = new Date();
  const dayCells = days.map((d) => {
    const inMonth = d.getMonth() === monthNum;
    const isToday = sameYMD(d, today);
    const onThisDay = eventsOnDay(occurrences, d).sort((a, b) => {
      const ta = (a.occ_start || new Date(a.start_at)).getTime();
      const tb = (b.occ_start || new Date(b.start_at)).getTime();
      return ta - tb;
    });
    const visible = onThisDay.slice(0, 3);
    const overflow = onThisDay.length - visible.length;
    return (
      <div
        key={d.toISOString()}
        data-testid="calendar-day-cell"
        data-in-month={inMonth ? "true" : "false"}
        data-day-iso={d.toISOString().slice(0, 10)}
        className={`border border-[var(--jc-line,#e2e8f0)] min-h-[92px] p-1.5 flex flex-col gap-1 ${
          inMonth ? "bg-white" : "bg-neutral-50 text-neutral-400"
        } ${isToday ? "outline outline-2 outline-[var(--jc-brand,#4f46e5)]/40" : ""}`}
      >
        <div className="flex items-center justify-between">
          <span
            data-testid={isToday ? "calendar-day-today" : undefined}
            className={`text-xs font-medium ${isToday ? "text-[var(--jc-brand,#4f46e5)]" : ""}`}
          >
            {d.getDate()}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          {visible.map((o) => (
            <EventPill key={`${o.id}-${o.occ_start.toISOString()}`} occ={o} t={t} lang={lang} dense />
          ))}
          {overflow > 0 ? (
            <div className="text-[10px] text-neutral-500" data-testid="calendar-day-overflow">
              {t("schedule.company.more_events", overflow)}
            </div>
          ) : null}
        </div>
      </div>
    );
  });
  const wd = t("schedule.company.weekday_short");
  const weekdayLabels = [wd.sun, wd.mon, wd.tue, wd.wed, wd.thu, wd.fri, wd.sat];
  return (
    <div data-testid="calendar-month-view">
      <div className="grid grid-cols-7 gap-0 mb-1">
        {weekdayLabels.map((w, i) => (
          <div
            key={i}
            className="text-[11px] uppercase tracking-wide text-neutral-500 px-1.5 py-1"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0 rounded-lg overflow-hidden border border-[var(--jc-line,#e2e8f0)]">
        {dayCells}
      </div>
    </div>
  );
};

const AgendaView = ({ occurrences, t, lang }) => {
  const now = new Date();
  const horizonEnd = addDays(startOfDay(now), 30);
  // Filter to occurrences within the next 30 days AND still relevant
  // (end_at >= now if today, else any that fall within the horizon).
  const relevant = occurrences
    .filter((o) => o.occ_end >= startOfDay(now) && o.occ_start <= horizonEnd)
    .sort((a, b) => a.occ_start.getTime() - b.occ_start.getTime());

  if (relevant.length === 0) {
    return (
      <div
        data-testid="calendar-agenda-empty"
        className="jc-card p-6 text-sm text-neutral-600"
      >
        <div className="font-medium mb-1">{t("schedule.company.empty_agenda")}</div>
        <div className="text-neutral-500 text-xs">{t("schedule.company.empty_hint")}</div>
      </div>
    );
  }

  // Group by yyyy-mm-dd of occ_start.
  const groups = new Map();
  for (const o of relevant) {
    const key = o.occ_start.toISOString().slice(0, 10);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(o);
  }

  return (
    <div data-testid="calendar-agenda-view" className="flex flex-col gap-4">
      {Array.from(groups.entries()).map(([key, list]) => {
        const day = new Date(`${key}T00:00:00`);
        const isToday = sameYMD(day, now);
        return (
          <section
            key={key}
            data-testid="calendar-agenda-day"
            data-agenda-day={key}
            className="jc-card p-4"
          >
            <header className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">
                {dateHeader(day, lang)}
                {isToday ? (
                  <span
                    data-testid="calendar-agenda-today-badge"
                    className="ml-2 text-[10px] uppercase tracking-wide bg-[var(--jc-brand,#4f46e5)] text-white rounded px-1.5 py-0.5"
                  >
                    {t("schedule.company.today")}
                  </span>
                ) : null}
              </h2>
              <span className="text-xs text-neutral-500">
                {list.length}
              </span>
            </header>
            <ul className="flex flex-col gap-2">
              {list.map((o) => {
                const projectId = o.project_id || o?.project?.id || "";
                const customer =
                  (o.project?.customer_name || "").trim() ||
                  t("schedule.company.customer_untitled");
                const assignee =
                  (o.assignee?.display_name || "").trim() ||
                  t("schedule.company.unassigned");
                const title = (o.title || "").trim() || customer;
                const startStr = o.all_day
                  ? t("schedule.field_all_day")
                  : timeLabel(o.occ_start?.toISOString?.() || o.start_at, lang);
                const endStr = o.all_day
                  ? ""
                  : timeLabel(o.occ_end?.toISOString?.() || o.end_at, lang);
                const rangeStr = o.all_day ? startStr : `${startStr} – ${endStr}`;
                return (
                  <li
                    key={`${o.id}-${o.occ_start.toISOString()}`}
                    data-testid="calendar-agenda-item"
                    data-event-id={o.id}
                    data-project-id={projectId}
                  >
                    <Link
                      to={projectId ? `/app/projects/${projectId}` : "#"}
                      className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 rounded border px-3 py-2 transition hover:brightness-95 ${statusTone(o.status)}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{title}</div>
                        <div className="text-[11px] text-neutral-600 truncate">
                          {customer} · {assignee}
                        </div>
                      </div>
                      <div className="text-[11px] font-medium whitespace-nowrap">{rangeStr}</div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const Schedule = () => {
  const { t, lang } = useLang();
  const isMobile = useIsMobile();
  // Default view: agenda on mobile, month on desktop. The user can flip
  // between them at will; we don't force-flip when the viewport crosses
  // the breakpoint mid-session so a resized-desktop stays on its choice.
  const [view, setView] = useState(() => (isMobile ? "agenda" : "month"));
  const [userTouchedView, setUserTouchedView] = useState(false);
  useEffect(() => {
    if (!userTouchedView) setView(isMobile ? "agenda" : "month");
  }, [isMobile, userTouchedView]);

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load assignees ONCE so the filter dropdown is populated. Owner-scoped
  // by the JWT — never leaks another contractor's crew list.
  useEffect(() => {
    let cancelled = false;
    api.get("/assignees")
      .then((r) => { if (!cancelled) setAssignees(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setAssignees([]); });
    return () => { cancelled = true; };
  }, []);

  // Fetch a wide window (previous month → +90 days) so the month view
  // has data for the current + next visible months. Cheap for MVP sizes.
  // Filters are forwarded to the backend so counts, month view, and
  // agenda view all reflect exactly the same filtered set from a single
  // authoritative source.
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const winStart = addDays(startOfMonth(cursor), -7);
    const winEnd = addDays(endOfMonth(cursor), 90);
    const params = {
      start: winStart.toISOString(),
      end: winEnd.toISOString(),
    };
    if (assigneeFilter) params.assignee_id = assigneeFilter;
    if (statusFilter) {
      params.status = statusFilter;
      // Canceled-event truthfulness: the backend hides canceled by
      // default. When the contractor explicitly asks for canceled we
      // must also lift the default filter.
      if (statusFilter === "canceled") params.include_canceled = true;
    }
    try {
      const r = await api.get("/schedule-events", { params });
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      setError(t("schedule.company.error"));
      setEvents([]);
      // toast is best-effort — never block render on it.
      try { toast.error(t("schedule.company.error")); } catch (_) { /* noop */ }
    } finally {
      setLoading(false);
    }
  }, [cursor, assigneeFilter, statusFilter, t]);

  useEffect(() => { load(); }, [load]);

  // Expand every fetched seed event into a flat occurrences list within
  // the visible window so both views share the same data source.
  const occurrences = useMemo(() => {
    if (!Array.isArray(events)) return [];
    const winStart = addDays(startOfMonth(cursor), -14);
    const winEnd = addDays(endOfMonth(cursor), 90);
    const out = [];
    for (const ev of events) {
      const occs = expandOccurrences(ev, winStart, winEnd);
      for (const o of occs) out.push(o);
    }
    return out;
  }, [events, cursor]);

  const goToday = () => setCursor(startOfMonth(new Date()));
  const goPrev = () => setCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goNext = () => setCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  // Counts used in the small overline strip.
  const nowDate = new Date();
  const todayStart = startOfDay(nowDate);
  const todayEnd = endOfDay(nowDate);
  const weekEnd = addDays(todayStart, 7);
  let todayCount = 0;
  let weekCount = 0;
  for (const o of occurrences) {
    if (o.occ_start <= todayEnd && o.occ_end >= todayStart) todayCount++;
    if (o.occ_start <= weekEnd && o.occ_end >= todayStart) weekCount++;
  }

  return (
    <DashboardLayout>
    <div data-testid="schedule-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <header className="flex flex-col gap-2 mb-5">
        <div className="flex items-center gap-2 text-[var(--jc-text-soft,#64748b)]">
          <CalendarIcon size={16} />
          <span className="text-xs uppercase tracking-wide">
            {t("schedule.company.page_title")}
          </span>
        </div>
        <h1
          data-testid="schedule-page-title"
          className="text-2xl sm:text-3xl font-semibold tracking-tight"
        >
          {t("schedule.company.page_title")}
        </h1>
        <p className="text-sm text-neutral-600 max-w-2xl">
          {t("schedule.company.page_subtitle")}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-600">
          <span
            data-testid="schedule-count-today"
            className="rounded-full border border-neutral-200 bg-white px-2 py-0.5"
          >
            {t("schedule.company.count_today", todayCount)}
          </span>
          <span
            data-testid="schedule-count-week"
            className="rounded-full border border-neutral-200 bg-white px-2 py-0.5"
          >
            {t("schedule.company.count_this_week", weekCount)}
          </span>
        </div>
      </header>

      <LeviScheduleAwareness />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <div className="flex items-center gap-1">
          <button
            data-testid="schedule-prev-btn"
            onClick={goPrev}
            aria-label={t("schedule.company.prev")}
            className="rounded border border-neutral-200 bg-white hover:bg-neutral-50 p-1.5"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            data-testid="schedule-today-btn"
            onClick={goToday}
            className="rounded border border-neutral-200 bg-white hover:bg-neutral-50 px-3 py-1 text-xs font-medium"
          >
            {t("schedule.company.today")}
          </button>
          <button
            data-testid="schedule-next-btn"
            onClick={goNext}
            aria-label={t("schedule.company.next")}
            className="rounded border border-neutral-200 bg-white hover:bg-neutral-50 p-1.5"
          >
            <ChevronRight size={16} />
          </button>
          <div
            data-testid="schedule-cursor-label"
            className="ml-2 text-sm font-medium first-letter:uppercase"
          >
            {monthLabel(cursor, lang)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-neutral-600">
            <FilterIcon size={12} className="opacity-70" />
            <label htmlFor="schedule-assignee-filter" className="sr-only">
              {t("schedule.company.filter_assignee_label")}
            </label>
            <select
              id="schedule-assignee-filter"
              data-testid="schedule-filter-assignee"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="rounded border border-neutral-200 bg-white px-2 py-1 text-xs font-medium min-h-[28px]"
              aria-label={t("schedule.company.filter_assignee_label")}
            >
              <option value="" data-testid="schedule-filter-assignee-all">
                {t("schedule.company.filter_assignee_all")}
              </option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {(a.display_name || "").trim() || t("schedule.company.unassigned")}
                </option>
              ))}
            </select>
            <label htmlFor="schedule-status-filter" className="sr-only">
              {t("schedule.company.filter_status_label")}
            </label>
            <select
              id="schedule-status-filter"
              data-testid="schedule-filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border border-neutral-200 bg-white px-2 py-1 text-xs font-medium min-h-[28px]"
              aria-label={t("schedule.company.filter_status_label")}
            >
              <option value="" data-testid="schedule-filter-status-all">
                {t("schedule.company.filter_status_all")}
              </option>
              <option value="scheduled">{t("schedule.status.scheduled")}</option>
              <option value="in_progress">{t("schedule.status.in_progress")}</option>
              <option value="completed">{t("schedule.status.completed")}</option>
              <option value="canceled">{t("schedule.status.canceled")}</option>
            </select>
          </div>
          <button
            data-testid="schedule-view-month-btn"
            onClick={() => { setUserTouchedView(true); setView("month"); }}
            aria-pressed={view === "month"}
            className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium transition ${
              view === "month"
                ? "border-[var(--jc-brand,#4f46e5)] bg-[var(--jc-brand,#4f46e5)] text-white"
                : "border-neutral-200 bg-white hover:bg-neutral-50"
            }`}
          >
            <LayoutGrid size={14} /> {t("schedule.company.view_month")}
          </button>
          <button
            data-testid="schedule-view-agenda-btn"
            onClick={() => { setUserTouchedView(true); setView("agenda"); }}
            aria-pressed={view === "agenda"}
            className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium transition ${
              view === "agenda"
                ? "border-[var(--jc-brand,#4f46e5)] bg-[var(--jc-brand,#4f46e5)] text-white"
                : "border-neutral-200 bg-white hover:bg-neutral-50"
            }`}
          >
            <ListIcon size={14} /> {t("schedule.company.view_agenda")}
          </button>
          <button
            data-testid="schedule-refresh-btn"
            onClick={load}
            aria-label={t("schedule.company.retry")}
            className="rounded border border-neutral-200 bg-white hover:bg-neutral-50 p-1.5"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div
          data-testid="schedule-loading"
          className="jc-card p-6 flex items-center gap-2 text-sm text-neutral-600"
        >
          <Loader2 size={16} className="animate-spin" />
          <span>{t("schedule.company.loading")}</span>
        </div>
      ) : error ? (
        <div data-testid="schedule-error" className="jc-card p-6 text-sm">
          <div className="text-red-600 mb-2">{error}</div>
          <button
            data-testid="schedule-retry-btn"
            onClick={load}
            className="rounded bg-neutral-900 text-white px-3 py-1 text-xs"
          >
            {t("schedule.company.retry")}
          </button>
        </div>
      ) : occurrences.length === 0 ? (
        <div data-testid="schedule-empty" className="jc-card p-6 text-sm text-neutral-600">
          <div className="font-medium mb-1">
            {assigneeFilter || statusFilter
              ? t("schedule.company.empty_filtered")
              : view === "month"
                ? t("schedule.company.empty_month")
                : t("schedule.company.empty_agenda")}
          </div>
          <div className="text-neutral-500 text-xs">
            {t("schedule.company.empty_hint")}
          </div>
        </div>
      ) : view === "month" ? (
        <MonthView cursor={cursor} occurrences={occurrences} t={t} lang={lang} />
      ) : (
        <AgendaView occurrences={occurrences} t={t} lang={lang} />
      )}
    </div>
    </DashboardLayout>
  );
};

export default Schedule;
